import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Users as UsersIcon, Plus, Trash2, Shield, ShieldOff, Edit2, X, Check, Library } from 'lucide-react'
import { users, libraries } from '../api/client'
import type { UserWithAccess } from '../api/client'
import { useAuthStore } from '../stores/auth'

interface EditingUser {
  id: string
  username: string
  password: string
  is_admin: boolean
}

export default function Users() {
  const queryClient = useQueryClient()
  const currentUser = useAuthStore((state) => state.user)
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingUser, setEditingUser] = useState<EditingUser | null>(null)
  const [managingAccessFor, setManagingAccessFor] = useState<string | null>(null)
  const [selectedLibraries, setSelectedLibraries] = useState<string[]>([])
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    is_admin: false,
  })

  const { data: userList, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: users.list,
  })

  const { data: libraryList } = useQuery({
    queryKey: ['libraries'],
    queryFn: libraries.list,
  })

  const createMutation = useMutation({
    mutationFn: () => users.create(newUser.username, newUser.password, newUser.is_admin),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowAddForm(false)
      setNewUser({ username: '', password: '', is_admin: false })
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: { id: string; updates: { username?: string; password?: string; is_admin?: boolean } }) =>
      users.update(data.id, data.updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: users.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const setAccessMutation = useMutation({
    mutationFn: (data: { userId: string; libraryIds: string[] }) =>
      users.setLibraryAccess(data.userId, data.libraryIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setManagingAccessFor(null)
      setSelectedLibraries([])
    },
  })

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate()
  }

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    const updates: { username?: string; password?: string; is_admin?: boolean } = {}
    const originalUser = userList?.find(u => u.id === editingUser.id)

    if (editingUser.username !== originalUser?.username) {
      updates.username = editingUser.username
    }
    if (editingUser.password) {
      updates.password = editingUser.password
    }
    if (editingUser.is_admin !== originalUser?.is_admin) {
      updates.is_admin = editingUser.is_admin
    }

    if (Object.keys(updates).length > 0) {
      updateMutation.mutate({ id: editingUser.id, updates })
    } else {
      setEditingUser(null)
    }
  }

  const startEditingUser = (user: UserWithAccess) => {
    setEditingUser({
      id: user.id,
      username: user.username,
      password: '',
      is_admin: user.is_admin,
    })
  }

  const startManagingAccess = (user: UserWithAccess) => {
    setManagingAccessFor(user.id)
    setSelectedLibraries(user.library_ids)
  }

  const toggleLibrary = (libraryId: string) => {
    setSelectedLibraries(prev =>
      prev.includes(libraryId)
        ? prev.filter(id => id !== libraryId)
        : [...prev, libraryId]
    )
  }

  const handleSaveAccess = () => {
    if (managingAccessFor) {
      setAccessMutation.mutate({ userId: managingAccessFor, libraryIds: selectedLibraries })
    }
  }

  // Non-admin users can't access this page
  if (!currentUser?.is_admin) {
    return (
      <div className="p-4 sm:p-6">
        <div className="card text-center py-12">
          <ShieldOff size={48} className="text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400 mb-2">Admin access required</p>
          <p className="text-dark-500 text-sm">
            You need admin privileges to manage users
          </p>
        </div>
      </div>
    )
  }

  const managingUser = userList?.find(u => u.id === managingAccessFor)

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Users</h1>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="btn btn-primary flex items-center justify-center gap-2"
        >
          <Plus size={18} />
          Add User
        </button>
      </div>

      {/* Add user form */}
      {showAddForm && (
        <form onSubmit={handleCreateSubmit} className="card mb-4 sm:mb-6 space-y-4">
          <h2 className="text-lg sm:text-xl font-semibold">Add New User</h2>

          {createMutation.error && (
            <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
              {(createMutation.error as any)?.response?.data?.error || 'Failed to create user'}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">
                Username
              </label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                className="input"
                placeholder="username"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-dark-300 mb-1">
                Password
              </label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="input"
                placeholder="password"
                required
              />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={newUser.is_admin}
                onChange={(e) => setNewUser({ ...newUser, is_admin: e.target.checked })}
                className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500"
              />
              <span className="text-sm text-dark-300">Admin privileges</span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="btn btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : 'Create User'}
            </button>
            <button
              type="button"
              onClick={() => setShowAddForm(false)}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Library access modal */}
      {managingAccessFor && managingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-dark-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold">Library Access for {managingUser.username}</h2>
              <button
                onClick={() => {
                  setManagingAccessFor(null)
                  setSelectedLibraries([])
                }}
                className="p-1 hover:bg-dark-700 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              {managingUser.is_admin ? (
                <p className="text-dark-400 text-sm">
                  Admins have access to all libraries by default.
                </p>
              ) : libraryList && libraryList.length > 0 ? (
                <div className="space-y-2">
                  {libraryList.map((library) => (
                    <label
                      key={library.id}
                      className="flex items-center gap-3 p-3 bg-dark-700 rounded-lg cursor-pointer hover:bg-dark-600"
                    >
                      <input
                        type="checkbox"
                        checked={selectedLibraries.includes(library.id)}
                        onChange={() => toggleLibrary(library.id)}
                        className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500"
                      />
                      <div className="flex-1">
                        <p className="font-medium">{library.name}</p>
                        <p className="text-sm text-dark-400">{library.library_type}</p>
                      </div>
                    </label>
                  ))}
                </div>
              ) : (
                <p className="text-dark-400 text-sm">No libraries available</p>
              )}
            </div>
            {!managingUser.is_admin && (
              <div className="p-4 border-t border-dark-700 flex gap-2">
                <button
                  onClick={handleSaveAccess}
                  disabled={setAccessMutation.isPending}
                  className="btn btn-primary flex-1"
                >
                  {setAccessMutation.isPending ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => {
                    setManagingAccessFor(null)
                    setSelectedLibraries([])
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* User list */}
      {isLoading ? (
        <div className="text-dark-400">Loading...</div>
      ) : userList && userList.length > 0 ? (
        <div className="grid gap-3 sm:gap-4">
          {userList.map((user) => (
            <div key={user.id} className="card">
              {editingUser?.id === user.id ? (
                <form onSubmit={handleEditSubmit} className="space-y-4">
                  {updateMutation.error && (
                    <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                      {(updateMutation.error as any)?.response?.data?.error || 'Failed to update user'}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-1">
                        Username
                      </label>
                      <input
                        type="text"
                        value={editingUser.username}
                        onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                        className="input"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-dark-300 mb-1">
                        New Password (leave blank to keep current)
                      </label>
                      <input
                        type="password"
                        value={editingUser.password}
                        onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
                        className="input"
                        placeholder="new password"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingUser.is_admin}
                        onChange={(e) => setEditingUser({ ...editingUser, is_admin: e.target.checked })}
                        className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-500 focus:ring-primary-500"
                        disabled={user.id === currentUser?.id}
                      />
                      <span className="text-sm text-dark-300">Admin privileges</span>
                      {user.id === currentUser?.id && (
                        <span className="text-xs text-dark-500">(cannot change your own admin status)</span>
                      )}
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={updateMutation.isPending}
                      className="btn btn-primary flex items-center gap-2"
                    >
                      <Check size={16} />
                      {updateMutation.isPending ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditingUser(null)}
                      className="btn btn-secondary flex items-center gap-2"
                    >
                      <X size={16} />
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      user.is_admin ? 'bg-primary-600/20' : 'bg-dark-700'
                    }`}>
                      {user.is_admin ? (
                        <Shield size={20} className="sm:w-6 sm:h-6 text-primary-400" />
                      ) : (
                        <UsersIcon size={20} className="sm:w-6 sm:h-6 text-dark-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-base sm:text-lg">{user.username}</h3>
                        {user.is_admin && (
                          <span className="px-2 py-0.5 text-xs bg-primary-600/30 text-primary-400 rounded-full">
                            Admin
                          </span>
                        )}
                        {user.id === currentUser?.id && (
                          <span className="px-2 py-0.5 text-xs bg-dark-600 text-dark-300 rounded-full">
                            You
                          </span>
                        )}
                      </div>
                      <p className="text-dark-500 text-xs mt-1">
                        Created: {new Date(user.created_at).toLocaleDateString()}
                        {!user.is_admin && user.library_ids.length > 0 && (
                          <span> · {user.library_ids.length} library access</span>
                        )}
                        {user.is_admin && (
                          <span> · All libraries</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => startManagingAccess(user)}
                      className="btn btn-secondary flex items-center gap-2"
                      title="Manage library access"
                    >
                      <Library size={18} />
                      <span className="sm:inline">Libraries</span>
                    </button>
                    <button
                      onClick={() => startEditingUser(user)}
                      className="btn btn-secondary flex items-center gap-2"
                      title="Edit user"
                    >
                      <Edit2 size={18} />
                      <span className="hidden sm:inline">Edit</span>
                    </button>
                    {user.id !== currentUser?.id && (
                      <button
                        onClick={() => {
                          if (confirm(`Are you sure you want to delete user "${user.username}"?`)) {
                            deleteMutation.mutate(user.id)
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="btn bg-red-600/20 hover:bg-red-600/30 text-red-400"
                        title="Delete user"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="card text-center py-12">
          <UsersIcon size={48} className="text-dark-500 mx-auto mb-4" />
          <p className="text-dark-400 mb-2">No users found</p>
          <p className="text-dark-500 text-sm">
            Add your first user to get started
          </p>
        </div>
      )}
    </div>
  )
}
